import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';
import type { CampaignData } from './campaign-actions';

export function CampaignManagement({ campaign }: { campaign: CampaignData }) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(campaign.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = ['draft', 'test_sent'].includes(campaign.status);

  async function rename() {
    setBusy(true);
    setError(null);
    try {
      await api(`/campaigns/${campaign.id}`, { method: 'PATCH', json: { name: name.trim() } });
      setRenaming(false);
      await revalidate();
    } catch (err) {
      setError(errMsg(err, 'Could not rename campaign'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !confirm(
        `Delete "${campaign.name}"? This cannot be undone. Previously sent test emails will remain in the send history.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api(`/campaigns/${campaign.id}`, { method: 'DELETE' });
      await navigate('/campaigns');
    } catch (err) {
      setError(errMsg(err, 'Could not delete campaign'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {renaming ? (
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void rename();
            }}
          >
            <input
              aria-label="New campaign name"
              className="h-9 rounded-md border px-2"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={120}
              disabled={busy}
              autoFocus
            />
            <Button
              className="cursor-pointer"
              type="submit"
              size="sm"
              disabled={busy || !name.trim()}
            >
              Save name
            </Button>
            <Button
              className="cursor-pointer"
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setRenaming(false)}
            >
              Cancel rename
            </Button>
          </form>
        ) : (
          <Button
            className="cursor-pointer"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setName(campaign.name);
              setRenaming(true);
              setError(null);
            }}
          >
            Rename campaign
          </Button>
        )}
        {canDelete && (
          <Button
            className="cursor-pointer"
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={() => void remove()}
          >
            Delete campaign
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
